<template>
	<div>
		<egranty-section-divider
			icon="receipt"
			:text="'Dokumenty finansowe projektu '.concat(projectData.title ? projectData.title : '')"
		/>

		<project-invoices-donation-warning
			v-if="showDonationWarning"
			:donation="requestedDonation"/>


		<v-layout row  align-center class="mt-3 ml-3" v-if="roles.isBidder() || roles.isSubBidder()">
			<v-btn
				:disabled="!canEditInvoice"
				@click="addInvoice">
				Dodaj dokument
			</v-btn>
			<XlsImportButton
				:disabled="!canEditInvoice"
				@reset="resetSearch"/>
			<v-btn
				@click="downloadInvoiceImportExample">
				Pobierz szablon importu
			</v-btn>
		</v-layout>

		<div v-on:keyup.enter="buildNewSearch">
			<v-layout row justify-center align-center>
				<v-flex sm2/>
				<v-flex sm3>
					<v-text-field
						browser-autocomplete="off"
						class="ma-2"
						v-model="searchFields.documentNumber"
						label="Numer dokumentu"
						aria-label="document number"
					></v-text-field>
				</v-flex>
				<v-flex sm3>
					<v-text-field
						browser-autocomplete="off"
						class="ma-2"
						v-model="searchFields.documentName"
						label="Rodzaj dokumentu"
						aria-label="document name"
					></v-text-field>
				</v-flex>
				<v-flex sm4>
					<v-layout align-center justify-center>
						<v-btn @click="buildNewSearch" color="primary" dark class="ma-2">Szukaj</v-btn>
						<v-btn @click="resetSearch" color="primary" dark class="ma-2">Resetuj</v-btn>
						<v-btn @click="exportData" color="primary" dark class="ma-2">Eksportuj</v-btn>
					</v-layout>
				</v-flex>
			</v-layout>
		</div>
		<v-data-table
			:headers="dataTable.headers"
			:rows-per-page-items="[10,20,25]"
			:loading="loading"
			:items="dataTable.itemList"
			:total-items="dataTable.itemCount"
			:pagination.sync="dataTable.pagination"
			item-key="key"
			class="elevation-1"
		>
			<template slot="headerCell" slot-scope="props">
				<div>
					{{props.header.text}}
				</div>
				<div>
					{{props.header.nextLineText}}
				</div>
			</template>
			<template slot="items" slot-scope="props">
				<td>{{ props.item.serialNumber }}</td>
				<td>{{ props.item.documentNumber}}</td>
				<td>{{ props.item.documentName }}</td>
				<td>{{ formatMoney(props.item.fromDotation) }}</td>
				<td>{{ formatMoney(props.item.fromOtherSources) }}</td>
				<td>{{ formatMoney(props.item.fromInterestsAndOther) }}</td>
				<td>{{ formatMoney(props.item.fromOwnInKind) }}</td>
				<td>{{ formatMoney(props.item.fromOwnHuman) }}</td>
				<td>{{ formatMoney(props.item.costsTotal) }}</td>
				<td>
					<v-layout row
						v-if="canEditInvoice">
						<egranty-icon-button
							icon="edit"
							color="blue"
							label="Edytuj dokument"
							for="project-invoices-view-edit-document"
							id="project-invoices-view-edit-document"
							@click="editInvoice(props.item)"
						></egranty-icon-button>
						<egranty-icon-button
							icon="delete"
							color="lightred"
							label="Usuń dokument"
							for="project-invoices-view-delete-document"
							id="project-invoices-view-delete-document"
							@click="deleteInvoice(props.item)"
						></egranty-icon-button>
					</v-layout>
				</td>
			</template>
		</v-data-table>
	</div>
</template>

<script>
	import {Numbers} from "@/utils/Numbers";
	import EgrantyIconButton from "../../../../genericComponents/EgrantyIconButton";
	import {Invoice} from "@/api/generated/Invoice";
	import EgrantySectionDivider from "../../../../genericComponents/EgrantySectionDivider";
	import {Pagination} from "@/utils/PaginationUtils";
	import {Api} from "@/api";
	import {Project} from "@/api/generated/Project";
	import {ExportUtils} from "@/utils/ExportUtils";
	import {FinalReport} from "@/api/generated/FinalReport";
	import ProjectInvoicesDonationWarning from "./ProjectInvoicesDonationWarning";
  import {Filters} from "@/utils/Filters";
  import XlsImportButton from "@/view/logged/project/invoices/XlsImportButton";
  import {Files} from "@/utils/Files";
  import {DataImport} from "@/api/generated/DataImport";

	export default {
		name: "ProjectInvoicesView",

		components: {XlsImportButton, ProjectInvoicesDonationWarning, EgrantySectionDivider, EgrantyIconButton},

    mixins: [Numbers, Filters, Files],

		data() {
			return {
				searchFields: Pagination.initSearchedFields([
					"projectId",
					"documentNumber",
					"documentName",
				]),
				loading: false,
				dataTable: {
					headers: [
						{text: "Nr pozycji", value: "serialNumber", sortable: true},
						{text: "Nr dokumentu", value: "documentNumber", sortable: true},
						{text: "Rodzaj dokumentu", value: "documentName", sortable: true},
						{text: "Z dotacji (w zł)", value: "fromDotation", sortable: true},
						{
							text: "Z innych środków",
							nextLineText: "finansowych (w zł)",
							value: "fromOtherSources",
							sortable: true
						},
						{
							text: "Z odsetek lub pozostałych",
							nextLineText: "przychodów (w zł)",
							value: "fromInterestsAndOther",
							sortable: true
						},
						{text: "Wkład rzeczowy (w zł)", value: "fromOwnInKind", sortable: true},
						{text: "Wkład osobowy (w zł)", value: "fromOwnHuman", sortable: true},
						{text: "Suma wszystkich", nextLineText: "kosztów (w zł)", value: "totalCosts", sortable: true},
						{text: "Akcje", sortable: false},
					],
					itemList: [],
					itemCount: 0,
					pagination: {
						page: 1,
						rowsPerPage: 25,
						sortBy: "",
						descending: false,
					},
				},

				projectId: this.$route.params.projectId,
				projectData: {},
				finalReportStatus: null,
				requestedDonation: null,
				invoicesDonationSum: null,
				search: "",
				canEditInvoice: false
			};
		},

		watch: {
			"dataTable.pagination": {
				handler(pagination) {
					this.getItemList();
          this.paginationRef = pagination;
				},
				deep: true,
			},
		},

		computed: {
			showDonationWarning () {
				if (!this.requestedDonation || !this.invoicesDonationSum) return false;
				return parseFloat(this.requestedDonation.toFixed(2)) < parseFloat(this.invoicesDonationSum.toFixed(2));
			}
		},

		created() {
      this.searchFields = this.retrieveFilters(this.searchFields);
      this.dataTable.pagination = this.retrievePagination(this.dataTable.pagination);
			this.searchFields.projectId = this.projectId;
			this.loadInvoices();
			this.getReportStatus();
			this.getProjectRequestedDonation();
			this.getInvoicesDonationSum();
		},

		methods: {

			buildNewSearch() {
				this.search = this.buildSearch();
				this.getItemList();
			},

			buildSearch() {
				return Pagination.buildSearchCriteria(this.searchFields, false);
			},

			getItemList() {
				let sort = this.buildSort();
				Api.get(this,
					"/invoice-list/list?search=" + this.search
						+ "&page=" + this.dataTable.pagination.page
						+ "&result=" + this.dataTable.pagination.rowsPerPage
						+ "&sort=".concat(sort), response => {
							this.search = this.buildSearch();
							this.dataTable.itemList = this.generateTableKeys(response.itemList);
							this.dataTable.itemCount = response.itemCount;
						});
			},

			generateTableKeys (items) {
				items.forEach(document => {
					this.$set(document, "key", document.id + '_' + document.serialNumber);
				});
				return items;
			},

			exportData() {
				this.search = this.buildSearch();
				let sort = this.buildSort();
				let path = "/invoice-list/export?search=" + this.search +
					"&sort=".concat(sort);
				ExportUtils.sendExportRequest(path, this);
			},

			buildSort() {
				let order = this.dataTable.pagination.descending === true ? "desc" : "asc";
				let columnName = this.dataTable.pagination.sortBy;
				if (order && columnName) {
					return columnName.concat(" ").concat(order);
				} else {
					return "";
				}
			},

			async resetSearch() {
				await Pagination.resetSearch(this.searchFields);
				this.searchFields.projectId = this.projectId;
				this.search = this.buildSearch();
				this.getItemList();
			},

			getReportStatus () {
				FinalReport.getCurrentState(this, this.$route.params.projectId, successResponse => {
					this.finalReportStatus = successResponse.status;
					this.canEditInvoice = successResponse.editFinalReportAllowed;
				}, errorResponse => {
					this.$messagesBus.$emit("error", " Nie udało się załadować statusu sprawozdania końcowego", errorResponse);
				});
			},

			getProjectRequestedDonation () {
				Project.getRequestedDotation(this, this.$route.params.projectId, successResponse => {
						this.requestedDonation = successResponse.requestedDotation;
					},
					errorResponse => {
						this.$messagesBus.$emit("error", "Nie udało się załadować wnioskowanej kwoty dotacji", errorResponse);
					});
			},

			getInvoicesDonationSum () {
				Invoice.getInvoicesDonationSum(this, this.$route.params.projectId, successResponse => {
						this.invoicesDonationSum = successResponse;
					},
					errorResponse => {
						this.$messagesBus.$emit("error", "Nie udało się załadować sumy dotacji dokumentów", errorResponse);
					});
			},

			loadInvoices() {
				Project.get(this, this.projectId, (result) => this.projectData = result);
				this.buildNewSearch();
			},

			addInvoice() {
				this.$router.push("/app/project/" + this.projectId + "/invoices/new");
			},

			editInvoice(invoice) {
				this.$router.push("/app/invoice/" + invoice.id);
			},

      downloadInvoiceImportExample() {
        DataImport.postExample(this,successResponse => {
          Files.methods.downloadBlobResponse(successResponse, "szablon.xlsx");
        }, errorResponse => {
          this.$messagesBus.$emit("error", "Nie udało się pobrać wzoru", errorResponse);
        }, {
          responseType: "blob",
        });
      },

			deleteInvoice(invoice) {
				this.$confirm("Czy na pewno chcesz usunąć dokument?",
					{
						icon: "delete",
						title: "Uwaga",
						color: "lightred",
						width: 500
					}).then(response => {
					if (response) {
						Invoice.delete(this, invoice.id, success => {
							this.getItemList();
							this.$messagesBus.$emit("success", "Pomyślnie usunięto dokument", success);
						}, error => {
							this.$messagesBus.$emit("error", "Nie udało się usunąć dokumentu", error);
						});
					}
				});
			},
			
		},
	};
</script>

<style scoped>
	/deep/ .v-datatable thead th.column {
		vertical-align: top;
	}
</style>
